import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import ar from './locales/ar.json';
// Ported alongside the Lease Agreement / Lease Renewal pages — these use the
// colon-namespaced t('common:key') / t('leaseAgreement:key') convention from
// their source project, kept as their own i18next namespaces rather than
// merged into the single flat `translation` namespace above.
import enCommon from './locales/en/common.json';
import enLeaseAgreement from './locales/en/leaseAgreement.json';
import arCommon from './locales/ar/common.json';
import arLeaseAgreement from './locales/ar/leaseAgreement.json';
// Ported alongside the rest of the Lease module (Proposal, Invoices, Receipts,
// Termination, Approvals, Reports, Finance Posting, Inventory Update) — same
// colon-namespaced convention as leaseAgreement above. These were previously
// missing here, which made every t('<ns>:key') call in those pages fall back
// to printing the raw key (e.g. "columns.property") instead of the label.
import enAgreementApproval from './locales/en/agreementApproval.json';
import enBulkReceipt from './locales/en/bulkReceipt.json';
import enFinancePosting from './locales/en/financePosting.json';
import enInstallmentInvoice from './locales/en/installmentInvoice.json';
import enInventoryUpdate from './locales/en/inventoryUpdate.json';
import enInvoiceApproval from './locales/en/invoiceApproval.json';
import enLeaseGeneralInvoice from './locales/en/leaseGeneralInvoice.json';
import enLeaseProposal from './locales/en/leaseProposal.json';
import enLeaseReceipt from './locales/en/leaseReceipt.json';
import enLeaseRevenue from './locales/en/leaseRevenue.json';
import enLeaseTermination from './locales/en/leaseTermination.json';
import enReports from './locales/en/reports.json';
import enReverseReceipt from './locales/en/reverseReceipt.json';
import enSingleInvoice from './locales/en/singleInvoice.json';
import arAgreementApproval from './locales/ar/agreementApproval.json';
import arBulkReceipt from './locales/ar/bulkReceipt.json';
import arFinancePosting from './locales/ar/financePosting.json';
import arInstallmentInvoice from './locales/ar/installmentInvoice.json';
import arInventoryUpdate from './locales/ar/inventoryUpdate.json';
import arInvoiceApproval from './locales/ar/invoiceApproval.json';
import arLeaseGeneralInvoice from './locales/ar/leaseGeneralInvoice.json';
import arLeaseProposal from './locales/ar/leaseProposal.json';
import arLeaseReceipt from './locales/ar/leaseReceipt.json';
import arLeaseRevenue from './locales/ar/leaseRevenue.json';
import arLeaseTermination from './locales/ar/leaseTermination.json';
import arReports from './locales/ar/reports.json';
import arReverseReceipt from './locales/ar/reverseReceipt.json';
import arSingleInvoice from './locales/ar/singleInvoice.json';

export const LANGUAGE_STORAGE_KEY = 'appLanguage';
export const RTL_LANGUAGES = ['ar'];

const applyDocumentDirection = (language) => {
  const isRtl = RTL_LANGUAGES.includes(language);
  document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
  document.documentElement.lang = language;
};

const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'en';

i18n.use(initReactI18next).init({
  resources: {
    en: {
      translation: en,
      common: enCommon,
      leaseAgreement: enLeaseAgreement,
      agreementApproval: enAgreementApproval,
      bulkReceipt: enBulkReceipt,
      financePosting: enFinancePosting,
      installmentInvoice: enInstallmentInvoice,
      inventoryUpdate: enInventoryUpdate,
      invoiceApproval: enInvoiceApproval,
      leaseGeneralInvoice: enLeaseGeneralInvoice,
      leaseProposal: enLeaseProposal,
      leaseReceipt: enLeaseReceipt,
      leaseRevenue: enLeaseRevenue,
      leaseTermination: enLeaseTermination,
      reports: enReports,
      reverseReceipt: enReverseReceipt,
      singleInvoice: enSingleInvoice,
    },
    ar: {
      translation: ar,
      common: arCommon,
      leaseAgreement: arLeaseAgreement,
      agreementApproval: arAgreementApproval,
      bulkReceipt: arBulkReceipt,
      financePosting: arFinancePosting,
      installmentInvoice: arInstallmentInvoice,
      inventoryUpdate: arInventoryUpdate,
      invoiceApproval: arInvoiceApproval,
      leaseGeneralInvoice: arLeaseGeneralInvoice,
      leaseProposal: arLeaseProposal,
      leaseReceipt: arLeaseReceipt,
      leaseRevenue: arLeaseRevenue,
      leaseTermination: arLeaseTermination,
      reports: arReports,
      reverseReceipt: arReverseReceipt,
      singleInvoice: arSingleInvoice,
    },
  },
  ns: [
    'translation',
    'common',
    'leaseAgreement',
    'agreementApproval',
    'bulkReceipt',
    'financePosting',
    'installmentInvoice',
    'inventoryUpdate',
    'invoiceApproval',
    'leaseGeneralInvoice',
    'leaseProposal',
    'leaseReceipt',
    'leaseRevenue',
    'leaseTermination',
    'reports',
    'reverseReceipt',
    'singleInvoice',
  ],
  defaultNS: 'translation',
  lng: savedLanguage,
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});

applyDocumentDirection(savedLanguage);

i18n.on('languageChanged', (language) => {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  applyDocumentDirection(language);
});

export default i18n;
